import React, { useEffect, useRef } from 'react';
import { FieldPhoneNumber, FieldTextInput, Form, H4, PrimaryButton } from '../../components';
import {
  composeValidators,
  maxLength,
  optionalEmailFormatValid,
  required,
} from '../../util/validators';

import { FormattedMessage, useIntl } from 'react-intl';
import { Form as FinalForm, Field } from 'react-final-form';
import { defaultCountries, parseCountry } from 'react-international-phone';
import classNames from 'classnames';

import css from './ListingPage.module.css';

const VALID_DIAL_CODES = new Set(defaultCountries.map(c => parseCountry(c).dialCode));
const MESSAGE_MAX_LENGTH = 500;

const SectionContactForm = props => {
  const {
    focusSignal,
    inProgress,
    ready,
    error,
    listingTitle,
    providerName,
    onResetInquiry,
    isVaarmarkt = false,
    ...rest
  } = props;

  const intl = useIntl();
  const formApiRef = useRef(null);

  useEffect(() => {
    if (ready) {
      formApiRef.current?.reset();
      const timer = setTimeout(() => {
        onResetInquiry?.();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [ready]);

  const requiredLabel = labelText => (
    <span className={css.requiredLabel}>
      <span>{labelText}</span>
      <span className={css.requiredAsterisk}>*</span>
    </span>
  );

  return (
    <section className={css.sectionContactForm}>
      <FinalForm
        {...rest}
        render={({ form, formId, handleSubmit }) => {
          formApiRef.current = form;
          const submitWithFocus = async e => {
            await handleSubmit(e);
            const { submitFailed, errors } = form.getState();
            if (submitFailed && errors) {
              const fieldIds = {
                senderName: `${formId}senderName`,
                senderEmail: `${formId}senderEmail`,
                senderPhoneNumber: formId ? `${formId}.senderPhoneNumber` : 'senderPhoneNumber',
                messageContent: `${formId}messageContent`,
              };
              for (const field of [
                'senderName',
                'senderEmail',
                'senderPhoneNumber',
                'messageContent',
              ]) {
                if (errors[field]) {
                  const el = document.getElementById(fieldIds[field]);
                  if (el) {
                    el.focus();
                    break;
                  }
                }
              }
            }
          };
          return (
            <Form className={css.form} onSubmit={submitWithFocus}>
              <H4 className={css.sectionTitle}>
                <FormattedMessage id="SectionContactForm.contact" />
              </H4>
              <p className={css.sectionSubtitle}>
                <FormattedMessage id="SectionContactForm.subtitle" />
              </p>

              <div className={css.fields}>
                <Field name="subject" component="input" type="hidden" />
                {/* Honeypot: hidden from real users; if filled → server auto-bans sender. */}
                <Field
                  name="website"
                  component="input"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: '-9999px',
                    width: '1px',
                    height: '1px',
                    opacity: 0,
                    pointerEvents: 'none',
                  }}
                />
                <div className={css.messageAbout}>
                  <span className={css.messageAboutLabel}>
                    <FormattedMessage id="SectionContactForm.messageAboutLabel" />
                  </span>
                  <span className={css.messageAboutTitle}>{listingTitle}</span>
                </div>
                <FieldTextInput
                  id={`${formId}senderName`}
                  name="senderName"
                  type="text"
                  label={requiredLabel(intl.formatMessage({ id: 'SectionContactForm.nameLabel' }))}
                  placeholder={intl.formatMessage({ id: 'SectionContactForm.namePlaceholder' })}
                  validate={required(intl.formatMessage({ id: 'SectionContactForm.nameRequired' }))}
                  showErrorOnSubmit
                />
                <FieldTextInput
                  id={`${formId}senderEmail`}
                  name="senderEmail"
                  type="text"
                  label={requiredLabel(intl.formatMessage({ id: 'SectionContactForm.emailLabel' }))}
                  placeholder={intl.formatMessage({ id: 'SectionContactForm.emailPlaceholder' })}
                  validate={composeValidators(
                    required(intl.formatMessage({ id: 'SectionContactForm.emailRequired' })),
                    optionalEmailFormatValid(
                      intl.formatMessage({ id: 'SectionContactForm.emailInvalid' })
                    )
                  )}
                  showErrorOnSubmit
                />
                <FieldPhoneNumber
                  id={formId ? `${formId}.senderPhoneNumber` : 'senderPhoneNumber'}
                  name="senderPhoneNumber"
                  label={intl.formatMessage({ id: 'SectionContactForm.phoneLabel' })}
                  placeholder={intl.formatMessage({ id: 'SectionContactForm.phonePlaceholder' })}
                  onChange={(e, dialCode) => {
                    form.change('senderPhoneNumberDialCode', dialCode);
                  }}
                  onMount={dialCode => {
                    form.change('senderPhoneNumberDialCode', dialCode);
                  }}
                  showErrorOnSubmit
                  validate={(v, allValues) => {
                    if (!v) return undefined;
                    const first4 = v.replace(/^\+/, '').slice(0, 4);
                    const isValidDialCode = [...VALID_DIAL_CODES].some(dc => first4.startsWith(dc));
                    if (!isValidDialCode) {
                      return intl.formatMessage({ id: 'SectionContactForm.phoneDialCodeInvalid' });
                    }
                    const dialCode = allValues?.senderPhoneNumberDialCode;
                    const rest = v.startsWith(`+${dialCode}`)
                      ? v.replace(`+${dialCode}`, '')
                      : v.replace(dialCode, '');

                    if (!rest) return undefined;

                    return /^\d{8,14}$/.test(rest)
                      ? undefined
                      : intl.formatMessage({ id: 'SectionContactForm.phoneInvalid' });
                  }}
                />
                <FieldTextInput
                  id={`${formId}messageContent`}
                  name="messageContent"
                  type="textarea"
                  label={requiredLabel(
                    intl.formatMessage({ id: 'SectionContactForm.messageLabel' })
                  )}
                  placeholder={intl.formatMessage({ id: 'SectionContactForm.messagePlaceholder' })}
                  validate={composeValidators(
                    required(intl.formatMessage({ id: 'SectionContactForm.messageRequired' })),
                    maxLength(
                      intl.formatMessage(
                        { id: 'SectionContactForm.messageTooLong' },
                        { maxLength: MESSAGE_MAX_LENGTH }
                      ),
                      MESSAGE_MAX_LENGTH
                    )
                  )}
                  showErrorOnSubmit
                />
                <PrimaryButton
                  className={classNames(css.button, css.contactSubmit, {
                    [css.highlightedbutton]: focusSignal,
                    [css.contactSubmitReady]: ready,
                    [css.contactSubmitInProgress]: inProgress,
                  })}
                  type="submit"
                  inProgress={inProgress}
                  ready={ready}
                >
                  <FormattedMessage id="SectionContactForm.sendMessage" />
                </PrimaryButton>

                <p
                  className={classNames(css.afterSubmit, {
                    [css.afterSubmitSuccess]: ready,
                  })}
                >
                  <FormattedMessage
                    id={
                      ready ? 'SectionContactForm.afterSubmit' : 'SectionContactForm.beforeSubmit'
                    }
                    values={{ providerName }}
                  />
                </p>
              </div>
            </Form>
          );
        }}
      />
    </section>
  );
};

export default SectionContactForm;
